// Frontend-only catalog helpers: search, synonyms, typo tolerance, derived facets.
// No backend/data changes — everything derives from existing product fields.

export function normalize(s) {
  return (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip Greek/Latin accents
    .replace(/ς/g, "σ")
    .trim();
}

// Synonyms: typing one term also matches the others.
const SYNONYMS = [
  ["αντηλιακο", "sunscreen", "spf", "αντιηλιακο"],
  ["βιταμινη", "vitamin", "βιταμινες", "vitamins"],
  ["ενυδατωση", "moisturizer", "hydration", "ενυδατικη", "ενυδατικο"],
  ["μαλλια", "hair", "σαμπουαν", "shampoo", "μαλλι"],
  ["βρεφικο", "baby", "βρεφικα", "μωρο", "παιδικο"],
  ["προσωπο", "face", "κρεμα προσωπου"],
  ["σωμα", "body"],
  ["ανοσοποιητικο", "immune", "immunity"],
  ["ακμη", "acne", "σπυρακια"],
  ["αντιγηρανση", "antiaging", "anti-aging", "ρυτιδες"],
  ["στοματικη", "oral", "οδοντοκρεμα", "toothpaste", "δοντια"],
  ["ορθοπεδικα", "orthopedic"],
  ["συμπληρωμα", "supplement", "συμπληρωματα"],
];

export function expandSynonyms(term) {
  const n = normalize(term);
  const out = new Set([n]);
  for (const group of SYNONYMS) {
    if (group.some((g) => n.includes(g) || g.includes(n))) group.forEach((g) => out.add(g));
  }
  return Array.from(out);
}

// Levenshtein distance for typo tolerance.
function lev(a, b) {
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 2) return 99;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return dp[m][n];
}

function fuzzyToken(token, target) {
  if (target.includes(token)) return true;
  const words = target.split(/\s+/);
  const tol = token.length >= 6 ? 2 : token.length >= 4 ? 1 : 0;
  return words.some((w) => lev(token, w) <= tol);
}

// Greeklish → Greek transliteration (frontend-only) so e.g. "antiliako" matches "αντηλιακό".
const GL_DIGRAPHS = [
  ["th", "θ"], ["ch", "χ"], ["ps", "ψ"], ["ks", "ξ"], ["ou", "ου"],
  ["gk", "γκ"], ["mp", "μπ"], ["nt", "ντ"], ["ts", "τσ"], ["tz", "τζ"],
  ["ai", "αι"], ["ei", "ει"], ["oi", "οι"], ["ev", "ευ"], ["af", "αφ"],
];
const GL_SINGLE = {
  a: "α", b: "β", c: "κ", d: "δ", e: "ε", f: "φ", g: "γ", h: "η", i: "ι",
  j: "ι", k: "κ", l: "λ", m: "μ", n: "ν", o: "ο", p: "π", q: "κ", r: "ρ",
  s: "σ", t: "τ", u: "υ", v: "β", w: "ω", x: "ξ", y: "υ", z: "ζ",
};

export function greeklish(input) {
  let s = normalize(input);
  if (!/[a-z]/.test(s)) return s; // only transliterate latin input
  for (const [lat, gr] of GL_DIGRAPHS) s = s.split(lat).join(gr);
  let out = "";
  for (const ch of s) out += GL_SINGLE[ch] ?? ch;
  return out;
}

// Score a product against a query (higher = better match). Returns -1 for no match.
export function scoreProduct(product, query) {
  const base = normalize(query);
  if (!base) return 0;
  const name = normalize(product.name);
  const brand = normalize(product.brand);
  const sku = normalize(product.sku);
  const desc = normalize(product.description);

  const queries = [base];
  const gl = greeklish(query);
  if (gl && gl !== base) queries.push(gl);

  const terms = new Set();
  queries.forEach((q) => expandSynonyms(q).forEach((t) => terms.add(t)));

  let score = -1;
  for (const t of terms) {
    if (sku && sku === t) score = Math.max(score, 100);
    if (name.startsWith(t)) score = Math.max(score, 90);
    if (brand && brand.startsWith(t)) score = Math.max(score, 80);
    if (name.includes(t)) score = Math.max(score, 70);
    if (brand && brand.includes(t)) score = Math.max(score, 60);
    if (sku && sku.includes(t)) score = Math.max(score, 55);
    if (desc && desc.includes(t)) score = Math.max(score, 40);
  }
  if (score === -1) {
    // typo tolerance on query tokens (original + greeklish) against name/brand
    for (const q of queries) {
      const qTokens = q.split(/\s+/).filter(Boolean);
      const ok = qTokens.every((tok) => fuzzyToken(tok, name) || (brand && fuzzyToken(tok, brand)));
      if (ok) { score = 30; break; }
    }
  }
  return score;
}

// Derived pharmacy facets from name + description (frontend-only, no data change).
const SKIN_TYPES = [
  { key: "λιπαρο", label: "Λιπαρό δέρμα", match: ["λιπαρ"] },
  { key: "ξηρο", label: "Ξηρό δέρμα", match: ["ξηρ"] },
  { key: "μεικτο", label: "Μεικτό δέρμα", match: ["μεικτ", "μικτ"] },
  { key: "ευαισθητο", label: "Ευαίσθητο δέρμα", match: ["ευαισθητ"] },
];
const FLAGS = [
  { key: "vegan", label: "Vegan", match: ["vegan"] },
  { key: "organic", label: "Organic / Βιολογικό", match: ["organic", "βιολογικ", "οργανικ"] },
  { key: "noparabens", label: "Χωρίς parabens", match: ["χωρισ paraben", "paraben free", "paraben-free", "no paraben", "χωρισ παραβεν"] },
  { key: "derm", label: "Δερματολογικά ελεγμένο", match: ["δερματολογικα ελεγμ", "dermatologically"] },
];

export function deriveAttributes(product) {
  const text = normalize(`${product.name} ${product.description} ${product.brand}`);
  const spf = [];
  const re = /spf\s?(\d{2})(\+)?/g;
  let m;
  while ((m = re.exec(text))) spf.push(`SPF ${m[1]}${m[2] || ""}`);
  const skin = SKIN_TYPES.filter((s) => s.match.some((x) => text.includes(x))).map((s) => s.key);
  const flags = FLAGS.filter((f) => f.match.some((x) => text.includes(x))).map((f) => f.key);
  return { spf: Array.from(new Set(spf)), skin, flags };
}

export const SKIN_LABELS = Object.fromEntries(SKIN_TYPES.map((s) => [s.key, s.label]));
export const FLAG_LABELS = Object.fromEntries(FLAGS.map((f) => [f.key, f.label]));
